import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMultiSelect, useSelect, useSwitch } from '@chilecompra/react-kit'
import { Card, DataTable, MultiSelect, Select, Switch, Typography } from '@chilecompra/react-kit/components'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { onGetAgilePurchaseBySectorThunk } from './AgilePurchaseBySector.actions'
import LineChart from '../../components/Charts/LineChart.v2'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { Content, Description, GridContainer, GridItem, Root, Title } from './AgilePurchaseBySector.styles'
import InfoAgilePurchaseBySector from '../../components/Infographics/InfoAgilePurchaseBySector'
import {
  INDICATORS_DICTIONARY,
  INDICATORS_OPTIONS,
  DISTRIBUTION_DICTIONARY,
  DISTRIBUTION_OPTIONS,
  DISTRIBUTION_TRANSLATION_DICTIONARY,
  OPTION_FILTER_SECTORS_AGILE,
  OPTION_ALL
} from '../../config/settings/constants'
import { generateYears } from '../../modules/utils/years'
import { getListMonth, getMonth } from '../../modules/utils/months'
import { formatAmount } from '../../modules/utils/formatters'

/**
 * The AgilePurchaseBySector's container.
 *
 */
const AgilePurchaseBySector = () => {
  const dispatch = useDispatch()

  const { endYear, latestUpdate, sectors: sectorOptions = [], loading } = useSelector(state => state.topNavbar)
  const { csv, csvAccumulated, data, enhancedData } = useSelector(state => state.agilePurchaseBySector)
  const months = useMemo(() => getListMonth(), [])
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: getMonth() === 0 ? endYear - 1 : endYear,
    changeCallback: updatedValue => {
      dispatch(onGetAgilePurchaseBySectorThunk({ year: updatedValue, sectorsIds: sectorsValues }))
    },
    overwriteCallback: () => endYear
  })
  const sectorOptionsTotal = useMemo(
    () => [
      {
        id: 0,
        name: 'Total Compra Ágil',
        value: 0
      },
      ...sectorOptions?.map(sector => ({
        id: sector.id,
        name: sector.name,
        value: sector.id
      }))
    ],
    [sectorOptions]
  )

  const [activeView, setActiveView] = useState(0)

  const { value: distributionValue, onChange: handleChangeDistribution } = useSelect({
    initialValue: DISTRIBUTION_DICTIONARY.Monthly
  })

  const { value: indicadorValue, onChange: handleChangeIndicador } = useSelect({
    initialValue: INDICATORS_DICTIONARY.orders
  })

  const {
    values: sectorsValues,
    onChange: handleChangeSectors,
    onDelete: handleDeleteSectors
  } = useMultiSelect({
    initialValues: OPTION_FILTER_SECTORS_AGILE,
    changeCallback: updatedValues =>
      dispatch(onGetAgilePurchaseBySectorThunk({ year: yearValue, sectorsIds: updatedValues })),
    formatCallbacks: [
      (updatedValues = []) => {
        const [firstValue] = updatedValues
        const indexOptionAll = OPTION_FILTER_SECTORS_AGILE.indexOf(OPTION_ALL)
        if (
          updatedValues[0] === OPTION_FILTER_SECTORS_AGILE[indexOptionAll + 1] &&
          OPTION_FILTER_SECTORS_AGILE.length === updatedValues.length + 1
        ) {
          return [-1]
        }
        if (OPTION_FILTER_SECTORS_AGILE.length === updatedValues.length + 1 && !updatedValues.includes(OPTION_ALL)) {
          return OPTION_FILTER_SECTORS_AGILE
        }
        if (updatedValues.includes(-1) && updatedValues.length > 1) {
          const index = updatedValues.indexOf(-1)
          updatedValues.splice(index, 1)
        }
        if (firstValue === OPTION_ALL && updatedValues.length === OPTION_FILTER_SECTORS_AGILE.length - 1) {
          const index = updatedValues.indexOf(OPTION_ALL)
          updatedValues.splice(index, 1)
        }
        if (updatedValues.length !== OPTION_FILTER_SECTORS_AGILE.length && updatedValues.length > 1) {
          updatedValues.sort((a, b) => a - b)
        }
        if (updatedValues.includes(OPTION_ALL)) {
          return OPTION_FILTER_SECTORS_AGILE
        }
        if (updatedValues.length === 0) {
          return [-1]
        }
        return updatedValues
      }
    ]
  })

  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()

  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, csvAccumulated, yearValue],
    captureImageCallback: () =>
      exportToImage('agile-purchase-by-sector-chart', `CompraAgil_CifrasPorSector_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `CompraAgil_CifrasPorSector_${yearValue}`)
  })
  const handleViewChange = newView => {
    setActiveView(newView)
    handleChangeView(newView)
  }
  const yearsOptions = useMemo(() => generateYears(endYear), [endYear])

  const processedData = useMemo(
    () =>
      enhancedData?.map(datum => {
        const amountMonthly = currencyValue ? datum.amountMonthlyUSD : datum.amountMonthlyCLP
        const amountAccumulated = currencyValue ? datum.amountAccumulatedUSD : datum?.amountAccumulatedCLP
        const updateData = {
          ...datum,
          Monthly: indicadorValue === INDICATORS_DICTIONARY.amount ? amountMonthly : datum.orders,
          Accumulated: indicadorValue === INDICATORS_DICTIONARY.amount ? amountAccumulated : datum.ordersAccumulated
        }
        return {
          ...updateData,
          data: updateData[DISTRIBUTION_TRANSLATION_DICTIONARY[distributionValue]]
        }
      }),
    [yearValue, currencyValue, distributionValue, indicadorValue, data]
  )

  const labels = useMemo(() => enhancedData.map(datum => ({ color: datum.color, name: datum.name })), [enhancedData])

  let headerDef
  let recordsDecoratorsDef
  if (distributionValue === DISTRIBUTION_DICTIONARY.Monthly) {
    headerDef = [
      { label: 'Meses', value: 'month' },
      { label: 'Sector del estado', value: 'name' },
      { label: 'N° de órdenes de compra', value: 'orders' },
      { label: 'Monto (CLP)', value: 'amountMonthlyCLP' },
      { label: 'Monto (USD)', value: 'amountMonthlyUSD' }
    ]
    recordsDecoratorsDef = [
      ({ month }) => month,
      ({ name }) => name,
      ({ orders }) => orders,
      ({ amountMonthlyCLP }) => `$${formatAmount(amountMonthlyCLP)}`,
      ({ amountMonthlyUSD }) => `USD ${formatAmount(amountMonthlyUSD)}`
    ]
  } else if (distributionValue === DISTRIBUTION_DICTIONARY.Accumulated) {
    headerDef = [
      { label: 'Meses', value: 'month' },
      { label: 'Sector del estado', value: 'name' },
      { label: 'N° de órdenes de compra', value: 'ordersAccumulated' },
      { label: 'Monto (CLP)', value: 'amountAccumulatedCLP' },
      { label: 'Monto (USD)', value: 'amountAccumulatedUSD' }
    ]
    recordsDecoratorsDef = [
      ({ month }) => month,
      ({ name }) => name,
      ({ ordersAccumulated }) => ordersAccumulated,
      ({ amountAccumulatedCLP }) => `$${formatAmount(amountAccumulatedCLP)}`,
      ({ amountAccumulatedUSD }) => `USD ${formatAmount(amountAccumulatedUSD)}`
    ]
  }

  useEffect(() => {
    if (!yearValue) return
    dispatch(onGetAgilePurchaseBySectorThunk({ year: yearValue, sectorsIds: sectorsValues }))
  }, [yearValue, distributionValue, indicadorValue])

  const handleTooltipFormatter = useCallback(
    function () {
      const { Monthly, Accumulated, month, name } = this.series.userOptions
      const index = this.point.index
      const monthName = month[index]?.name || month[index]
      let value
      if (indicadorValue === INDICATORS_DICTIONARY.orders) {
        value = distributionValue === DISTRIBUTION_DICTIONARY.Monthly ? Monthly[index] : Accumulated[index]
      } else {
        value = currencyValue ? `USD ${formatAmount(Monthly[index])}` : `$${formatAmount(Monthly[index])}`
      }

      return `
        <ul>
          <li>
            - <strong>${name}</strong> en <strong>${monthName}</strong>
          </li>
           <br />
          <li>
            - <strong>${value} </strong> transados en ${yearValue}.
          </li>
           <br />
        </ul>
      `
    },
    [yearValue, distributionValue, indicadorValue, currencyValue]
  )

  return (
    <Root>
      <Content>
        <Typography
          color="primary"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="21px"
          margin="0 0 24px"
          variant="body1"
        >
          Compras realizadas a través de Compra Ágil
        </Typography>
        <GridContainer>
          <GridItem xs={12}>
            <Title>Cifras por sector del estado</Title>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={5}>
            <Description>
              Revisa los montos y la cantidad de órdenes de compra realizadas mediante Compra Ágil por mes. Filtrando
              por sectores del Estado.
            </Description>
          </GridItem>
        </GridContainer>

        <DataLayout
          disableCaptureImage={disableCaptureImage}
          disableDownloadFile={disableDownloadFile}
          header={
            <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
              <GridItem md={10} xs={12}>
                <GridContainer alignItems="center" spacing={1}>
                  <GridItem md={3} xs={12}>
                    <Select
                      label="Año"
                      loading={loading}
                      options={yearsOptions}
                      value={yearValue}
                      onChange={handleChangeYear}
                    />
                  </GridItem>
                  <GridItem md={3} xs={12}>
                    <MultiSelect
                      label="Sector del estado"
                      loading={loading}
                      options={sectorOptionsTotal}
                      values={sectorsValues}
                      onChange={handleChangeSectors}
                      onDelete={handleDeleteSectors}
                    />
                  </GridItem>
                  {activeView === DataLayoutViews.CHART && (
                    <GridItem md={3} xs={12}>
                      <Select
                        label="Indicador"
                        loading={loading}
                        options={INDICATORS_OPTIONS}
                        value={indicadorValue}
                        onChange={handleChangeIndicador}
                      />
                    </GridItem>
                  )}
                  <GridItem md={3} xs={12}>
                    <Select
                      label="Distribución"
                      loading={loading}
                      options={DISTRIBUTION_OPTIONS}
                      value={distributionValue}
                      onChange={handleChangeDistribution}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              {indicadorValue === INDICATORS_DICTIONARY.amount && activeView === DataLayoutViews.CHART && (
                <GridItem md={2} xs={12}>
                  <Switch
                    checked={currencyValue}
                    color="primary"
                    labelLeft="CLP"
                    labelRight="USD"
                    onChange={handleChangeCurrency}
                    margin="0 0 0 32px"
                  />
                </GridItem>
              )}
            </GridContainer>
          }
          infographic={<InfoAgilePurchaseBySector />}
          noData={!loading && data?.length === 0}
          noDataText="El organismo comprador no cuenta con información relacionada a sus proveedores durante el año seleccionado."
          ref={dataLayoutRef}
          updatedDate={latestUpdate}
          onCaptureImage={handleCaptureImage}
          onChangeView={handleViewChange}
          onDownloadFile={handleDownloadFile}
        >
          <DataLayoutChild view={DataLayoutViews.CHART}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 1px">
                <GridItem xs={12}>
                  <LineChart
                    key={`chart-${distributionValue}-${currencyValue}-${indicadorValue}`}
                    categories={months}
                    data={processedData}
                    id="agile-purchase-by-sector-chart"
                    labels={labels}
                    tooltipFormatter={handleTooltipFormatter}
                  />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>
          <DataLayoutChild view={DataLayoutViews.TABLE}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 20px">
                <GridItem xs={12}>
                  <DataTable
                    headers={headerDef}
                    headersCellsProps={[
                      { width: '20%' },
                      { enableSort: true, width: '20%' },
                      { enableSort: true, width: '20%' },
                      { enableSort: true, width: '20%' },
                      { enableSort: true, width: '20%' }
                    ]}
                    initialRows={15}
                    loading={loading}
                    records={data}
                    recordsDecorators={recordsDecoratorsDef}
                  />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>
        </DataLayout>
      </Content>
    </Root>
  )
}

export default AgilePurchaseBySector
